class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');

    // Initial lazy section load only once — never again after ATC / renderContents.
    if (this.dataset.sectionLoaded === 'true') return;
    if (this.querySelector('form.bella-cart, form#cart')) {
      this.dataset.sectionLoaded = 'true';
      return;
    }

    this._sectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    this._sectionObserver.observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    if (this._sectionObserver) {
      this._sectionObserver.disconnect();
      this._sectionObserver = null;
    }

    // Drawer already filled by ATC section render — don't replace again (causes upsell reload loop).
    if (this.dataset.sectionLoaded === 'true') return;
    if (this.querySelector('form.bella-cart .bella-cart__items li, form.bella-cart cart-items li')) {
      this.dataset.sectionLoaded = 'true';
      return;
    }

    const fetchUrl = this.dataset.url;
    fetch(fetchUrl)
      .then((response) => response.text())
      .then((html) => {
        if (this.dataset.sectionLoaded === 'true') return;
        // ATC may have filled the drawer while this request was in flight.
        if (this.querySelector('form.bella-cart, form#cart')) {
          this.dataset.sectionLoaded = 'true';
          return;
        }
        const host = document.getElementById('mini-cart');
        if (!host) return;
        host.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
        this.dataset.sectionLoaded = 'true';
        this.stabilizeRecommendations(host);
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }

    this.drawer.openMenuDrawer();
  }

  stripRecsAnimate(el) {
    if (!el) return;
    if (window.BellaCartRecs && typeof window.BellaCartRecs.stripAnimate === 'function') {
      window.BellaCartRecs.stripAnimate(el);
      return;
    }
    el.removeAttribute('data-animate');
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
    });
  }

  stabilizeRecommendations(host) {
    if (!host) return;
    const recs = host.querySelector('cart-recommendations');
    if (!recs) return;

    const url = recs.dataset.url;
    const hasItems = !!recs.querySelector('.bella-cart__recs-item');
    if (hasItems) {
      recs.dataset.loaded = 'true';
      delete recs.dataset.loading;
      if (url && window.BellaCartRecs) {
        window.BellaCartRecs.setCached(url, recs.innerHTML);
      }
      return;
    }

    const cached = url && window.BellaCartRecs ? window.BellaCartRecs.getCached(url) : '';
    if (cached) {
      recs.innerHTML = cached;
      recs.dataset.loaded = 'true';
      delete recs.dataset.loading;
    }
  }

  /**
   * Patch items/footer/icons only — keep live <cart-recommendations> node.
   * options.preserveAnimate: keep data-animate so line items can entrance-animate on open.
   */
  quietMergeFromSections(parsedState, options) {
    options = options || {};
    const preserveAnimate = options.preserveAnimate === true;

    const host = document.getElementById('mini-cart');
    if (!host || !parsedState || !parsedState.sections) return false;

    const cartSectionId = this.dataset.sectionId || 'mini-cart';
    const miniHtml = parsedState.sections[cartSectionId];
    if (!miniHtml) return false;

    const form =
      host.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart') ||
      host.querySelector('form#cart');
    if (!form) return false;

    // Prefer Bella quiet-merge when present (preorder preserve + date stamp).
    if (typeof window.BellaCartQuietMergeFromSections === 'function') {
      try {
        if (window.BellaCartQuietMergeFromSections(parsedState)) return true;
      } catch (err) {
        /* fall through */
      }
    }
    // Upsell restore in flight — never fall through to a destructive full replace.
    if (typeof window.BellaCartUpsellBusy === 'function' && window.BellaCartUpsellBusy()) {
      return true;
    }

    // Snapshot preorder before this theme merge replaces line items.
    if (typeof window.BellaCartArmPreorderPreserve === 'function') {
      try {
        window.BellaCartArmPreorderPreserve();
      } catch (err) {
        /* ignore */
      }
    }

    const doc = new DOMParser().parseFromString(miniHtml, 'text/html');
    const newForm =
      doc.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart') ||
      doc.querySelector('form#cart');
    if (!newForm) return false;

    const liveRecs = host.querySelector('cart-recommendations');

    const newCount = newForm.querySelector('.bella-cart__item-count');
    const oldCount = form.querySelector('.bella-cart__item-count');
    if (newCount && oldCount) oldCount.textContent = newCount.textContent;

    const newItems = newForm.querySelector('cart-items');
    const oldItems = form.querySelector('cart-items');
    if (newItems && oldItems && oldItems.parentNode) {
      const imported = document.importNode(newItems, true);
      if (!preserveAnimate) {
        imported.querySelectorAll('[data-animate]').forEach((el) => {
          el.removeAttribute('data-animate');
          Array.from(el.attributes).forEach((attr) => {
            if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
          });
        });
      }
      oldItems.replaceWith(imported);
    }

    const newFooter = newForm.querySelector('.mini-cart__footer.bella-cart__footer, .bella-cart__footer');
    const oldFooter = form.querySelector('.mini-cart__footer.bella-cart__footer, .bella-cart__footer');
    if (newFooter && oldFooter) {
      const tmp = document.createElement('div');
      tmp.innerHTML = newFooter.innerHTML;
      if (!preserveAnimate) {
        tmp.querySelectorAll('[data-animate]').forEach((el) => {
          el.removeAttribute('data-animate');
          Array.from(el.attributes).forEach((attr) => {
            if (attr.name.indexOf('data-animate-delay') === 0) el.removeAttribute(attr.name);
          });
        });
      }
      oldFooter.innerHTML = tmp.innerHTML;
    }

    form.classList.toggle('is-empty', newForm.classList.contains('is-empty'));
    [
      'data-subtotal-cents',
      'data-free-ship-min-cents',
      'data-dubai-fee-cents',
      'data-other-fee-cents',
      'data-bella-cart-mode'
    ].forEach((attr) => {
      if (newForm.hasAttribute(attr)) form.setAttribute(attr, newForm.getAttribute(attr));
    });

    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
      const el = document.getElementById(id);
      const html = parsedState.sections[id];
      if (el && html) el.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
    });

    if (liveRecs && !preserveAnimate) this.stripRecsAnimate(liveRecs);

    if (typeof window.BellaCartPruneUpsell === 'function') {
      window.BellaCartPruneUpsell(host);
    } else if (window.BellaCartRecs && typeof window.BellaCartRecs.pruneHost === 'function') {
      window.BellaCartRecs.pruneHost(liveRecs);
    }

    // Preserve cart delivery date across PDP ATC quiet-merge + stamp new lines.
    // Also snapshot preorder lines before merge so normal ATC cannot wipe them.
    if (typeof window.BellaCartArmPreorderPreserve === 'function') {
      try {
        window.BellaCartArmPreorderPreserve();
      } catch (err) {
        /* ignore */
      }
    }
    if (typeof window.BellaCartEnsureDateAfterMerge === 'function') {
      try {
        const liveSource = form.dataset.bellaDateSource || '';
        const liveIso = form.dataset.deliveryDate || '';
        const liveDisplay = form.dataset.deliveryDateDisplay || '';
        if (liveSource === 'cart' && liveIso) {
          form.dataset.bellaDateSource = 'cart';
          form.dataset.deliveryDate = liveIso;
          if (liveDisplay) form.dataset.deliveryDateDisplay = liveDisplay;
        }
        window.BellaCartEnsureDateAfterMerge({ lockMs: 1800 });
      } catch (err) {
        /* ignore */
      }
    }

    return true;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.dataset.sectionLoaded = 'true';

    if (this._sectionObserver) {
      try {
        this._sectionObserver.disconnect();
      } catch (e) {}
      this._sectionObserver = null;
    }

    // During Bella upsell restore, never wipe the drawer with empty/partial HTML.
    if (typeof window.BellaCartUpsellBusy === 'function' && window.BellaCartUpsellBusy()) {
      if (typeof window.BellaCartQuietMergeFromSections === 'function') {
        try {
          window.BellaCartQuietMergeFromSections(parsedState);
        } catch (err) {
          /* ignore */
        }
      }
      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      this.open();
      return;
    }

    const host = document.getElementById('mini-cart');
    const existingForm =
      host &&
      (host.querySelector('form#cart.bella-cart, form.bella-cart#cart, form.bella-cart') ||
        host.querySelector('form#cart'));
    const hadCartForm = !!existingForm;
    const wasEmpty = !!(existingForm && existingForm.classList.contains('is-empty'));
    const drawerAlreadyOpen = !!(
      this.drawer &&
      this.drawer.querySelector('details') &&
      this.drawer.querySelector('details').hasAttribute('open')
    );

    // Quiet-merge only when cart already has items. Empty → first item needs full
    // section replace so stepper/delivery + line-item entrance animation work.
    // When opening the drawer, keep data-animate on line items.
    if (
      hadCartForm &&
      !wasEmpty &&
      this.quietMergeFromSections(parsedState, { preserveAnimate: !drawerAlreadyOpen })
    ) {
      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      this.open();
      return;
    }

    const prevRecs = host && host.querySelector('cart-recommendations');
    const prevRecsHtml = prevRecs && prevRecs.innerHTML.trim() ? prevRecs.innerHTML : '';
    const prevTrack = prevRecs && prevRecs.querySelector('.bella-cart__recs-track');
    const prevScroll = prevTrack ? prevTrack.scrollLeft : 0;

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      const html = parsedState.sections && parsedState.sections[section.section];
      if (el && html) {
        el.innerHTML = this.getSectionInnerHTML(html, section.selector);
      }
    });

    // Prefer fresh Liquid (filters in-cart products). Only reuse old upsell DOM
    // when the new section has no recommendation cards yet.
    if (host && prevRecsHtml) {
      const nextRecs = host.querySelector('cart-recommendations');
      if (nextRecs) {
        const nextHasItems = !!nextRecs.querySelector('.bella-cart__recs-item');
        if (!nextHasItems) {
          nextRecs.innerHTML = prevRecsHtml;
          nextRecs.dataset.loaded = 'true';
          delete nextRecs.dataset.loading;
        } else {
          nextRecs.dataset.loaded = 'true';
          delete nextRecs.dataset.loading;
          if (window.BellaCartRecs && nextRecs.dataset.url) {
            window.BellaCartRecs.setCached(nextRecs.dataset.url, nextRecs.innerHTML);
          }
        }
        const track = nextRecs.querySelector('.bella-cart__recs-track');
        if (track && prevScroll) track.scrollLeft = prevScroll;
        if (drawerAlreadyOpen) this.stripRecsAnimate(nextRecs);
      }
    } else if (host) {
      this.stabilizeRecommendations(host);
    }

    if (typeof window.BellaCartPruneUpsell === 'function') {
      window.BellaCartPruneUpsell(host);
    }

    if (typeof window.BellaCartEnsureDateAfterMerge === 'function') {
      try {
        window.BellaCartEnsureDateAfterMerge({ lockMs: 1800 });
      } catch (err) {
        /* ignore */
      }
    }

    document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
    this.open();
  }

  getSectionsToRender() {
    const cartSectionId = this.dataset.sectionId || 'mini-cart';
    return [
      {
        id: 'mini-cart',
        section: cartSectionId,
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);
